/**
 * Saves as files the player can keep, move and share.
 *
 * The cheapest useful answer to "my save only lives in this browser": a save
 * is already exactly one ArrayBuffer, so writing it to disk needs no server,
 * no account and no change to the format. Copy the file to another machine and
 * import it there.
 *
 * Browser-specific, so it lives in platform/ rather than core/.
 */

/** Our own extension, so the OS does not offer to open it with something else. */
export const SAVE_EXTENSION = ".vyzh";

/** e.g. vyzhyvannia-2026-09-05-2231.vyzh */
export function suggestedFilename(savedAt: number): string {
  const d = new Date(savedAt);
  const pad = (n: number): string => String(n).padStart(2, "0");
  const stamp =
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
    `-${pad(d.getHours())}${pad(d.getMinutes())}`;
  return `vyzhyvannia-${stamp}${SAVE_EXTENSION}`;
}

/** Hand the bytes to the browser as a download. */
export function downloadSave(bytes: ArrayBuffer, filename: string): void {
  const url = URL.createObjectURL(new Blob([bytes], { type: "application/octet-stream" }));
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  // Must be in the document for the click to count in some browsers.
  document.body.appendChild(link);
  link.click();
  link.remove();
  // Revoking immediately can cancel the download in Safari; a tick is enough.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/**
 * Ask the player for a save file.
 *
 * Resolves to null if they cancel. Must be called from a user gesture - a
 * keypress or a click - or the browser will refuse to open the dialog.
 */
export function pickSaveFile(): Promise<ArrayBuffer | null> {
  return new Promise((resolve) => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = SAVE_EXTENSION;
    input.style.display = "none";

    let settled = false;
    const finish = (value: ArrayBuffer | null): void => {
      if (settled) return;
      settled = true;
      input.remove();
      resolve(value);
    };

    input.addEventListener("change", () => {
      const file = input.files?.[0];
      if (file === undefined) { finish(null); return; }
      file.arrayBuffer().then(finish).catch(() => finish(null));
    });
    // Fired when the dialog is dismissed. Without it a cancelled pick would
    // leave this promise pending forever.
    input.addEventListener("cancel", () => finish(null));

    document.body.appendChild(input);
    input.click();
  });
}
